import { useEffect, useState } from "react";
import { API_BASE } from "../config";

export type OperatorItem = {
  label: string;
  value: string;
  category?: string;
  is_filter?: boolean;
  is_comparison?: boolean;
};

let cached: OperatorItem[] | null = null;
let cachedPromise: Promise<void> | null = null;

/** Synchronous lookup using the shared cache — safe to use outside React hooks */
export function getOperatorCategoryStatic(value: string | undefined): string | null {
  return cached?.find((op) => op.value === value)?.category ?? null;
}

export function getOperatorFlagsStatic(value: string | undefined): { isFilter: boolean; isComparison: boolean } {
  const op = cached?.find((o) => o.value === value);
  return {
    isFilter: op?.category === 'filter' || value === 'Filter',
    isComparison: op?.category === 'comparison',
  };
}

export function getDefaultOperatorStatic(): string {
  return cached?.[0]?.value ?? '';
}

export async function preloadOperators() {
  if (cached) return;
  if (cachedPromise) return cachedPromise;

  cachedPromise = (async () => {
    try {
      const res = await fetch(`${API_BASE}/operators`);
      if (!res.ok) throw new Error(`GET /operators → HTTP ${res.status}`);
      const json = await res.json();

      cached = json.operators.sort((a: OperatorItem, b: OperatorItem) => {
        if (a.category !== b.category) {
          return (a.category ?? '').localeCompare(b.category ?? '');
        }
        return a.label.localeCompare(b.label);
      });
    } finally {
      cachedPromise = null;
    }
  })();

  return cachedPromise;
}

export function useOperators() {
  const [operators, setOperators] = useState<OperatorItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    (async () => {
      try {
        if (cached) {
          setOperators(cached);
        } else {
          await preloadOperators();
          if (cached) setOperators(cached);
        }
      } catch (e: unknown) {
        if (e instanceof Error) {
          setError(e.message);
        } else {
          setError("Unknown error");
        }
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  return {
    operators,
    loading,
    error,
    getOperatorCategory: (value: string | undefined) =>
      cached?.find((op) => op.value === value)?.category ?? null,
    getOperatorFlags: (value: string | undefined) => {
      const op = cached?.find((o) => o.value === value);
      return {
        isFilter: op?.category === 'filter' || value === 'Filter',
        isComparison: op?.category === 'comparison',
      };
    },
    getOperatorByValue: (value: string | undefined): OperatorItem | undefined => {
      return cached?.find((op) => op.value === value);
    },
  };
}
